<template>
  <div class="h-full w-full p-4">
    <FileUpload
      ref="fileUploadRef"
      :accept="acceptString"
      multiple
      custom-upload
      :pt="{
        root: { class: 'h-full w-full bg-surface-900 text-surface-0' },
        header: {
          class:
            'flex justify-center !border-none !px-[1.125rem] !pb-3 !pt-[1.125rem]',
        },
        input: { class: 'bg-surface-800 border-surface-600 text-surface-0' },
        chooseButton: {
          class:
            'bg-surface-700 border-surface-600 hover:bg-surface-600 text-surface-0',
        },
        uploadButton: {
          class: 'bg-green-600 border-green-600 text-white hover:bg-green-700',
        },
        cancelButton: {
          class:
            'bg-surface-700 border-surface-600 hover:bg-surface-600 text-surface-0',
        },
      }"
      @select="handleSelect"
      @upload="handleUpload"
    >
      <template #header>
        <StwButton size="sm" @click="emit('changeType')">
          <i class="pi pi-arrow-left" />
          Choose Another Type
        </StwButton>
      </template>

      <template
        #content="{ files, uploadedFiles, removeFileCallback, messages }"
      >
        <div
          :class="[
            'flex flex-col gap-8 pt-4',
            {
              'flex-1':
                files.length > 0 ||
                uploadedFiles.length > 0 ||
                (messages && messages.length > 0),
            },
          ]"
        >
          <StwMessage
            v-for="message of messages"
            :key="message"
            :class="{ 'mb-8': !files.length && !uploadedFiles.length }"
            severity="error"
          >
            {{ message }}
          </StwMessage>

          <div v-if="files.length > 0" class="flex w-full justify-center">
            <div class="w-full max-w-md text-center">
              <div class="mb-4 text-lg font-bold">Selected File(s)</div>

              <div
                class="bg-surface-900/50 rounded-xl p-6 shadow-sm ring-1 ring-white/10 backdrop-blur"
              >
                <template v-if="files.length === 1">
                  <div class="mb-2 break-all text-xl font-semibold">
                    {{ files[0].name }}
                  </div>
                  <div class="mb-2 text-sm opacity-80">
                    Type: {{ getFileTypeString(files[0]) }}
                  </div>
                  <div class="mb-4 text-sm opacity-80">
                    Size: {{ getFileSizeString(files[0].size) }}
                  </div>
                </template>

                <template v-else>
                  <div class="mb-4 max-h-40 overflow-y-auto text-left">
                    <div class="mb-2 text-sm font-semibold">
                      {{ files.length }} files selected:
                    </div>
                    <div
                      v-for="(file, index) in files"
                      :key="index"
                      class="mb-1 flex items-center justify-between text-xs"
                    >
                      <span class="max-w-[70%] truncate">{{ file.name }}</span>
                      <div class="flex items-center">
                        <span class="mr-2">
                          {{ getFileSizeString(file.size) }}
                        </span>
                        <Button
                          icon="pi pi-trash"
                          severity="danger"
                          text
                          rounded
                          aria-label="Delete"
                          size="small"
                          :pt="{
                            root: {
                              class:
                                'text-red-400 hover:bg-red-900/20 hover:text-red-300',
                            },
                          }"
                          @click="removeFileCallback(index)"
                        />
                      </div>
                    </div>
                  </div>
                </template>

                <div
                  v-if="canPerformScan && quotas"
                  class="mb-4 rounded border border-blue-500/30 bg-blue-900/20 p-2 text-sm text-blue-300"
                >
                  Current scans: {{ calculateQuotaDisplay(quotas) }}
                </div>

                <div
                  v-if="canPerformScan"
                  class="mb-4 rounded border border-amber-500/30 bg-amber-900/20 p-2 text-sm text-amber-300"
                >
                  This action will consume 1 scan from your account.
                </div>

                <div
                  v-if="quotas"
                  class="mb-4 rounded border p-3 text-sm"
                  :class="
                    isStorageFull
                      ? 'border-red-500/30 bg-red-900/20 text-red-300'
                      : storagePercentage >= 80
                        ? 'border-amber-500/30 bg-amber-900/20 text-amber-300'
                        : 'border-purple-500/30 bg-purple-900/20 text-purple-300'
                  "
                >
                  <div class="mb-2 font-semibold">
                    Storage: {{ storageDisplay }}
                  </div>

                  <template v-if="isStorageFull">
                    <div class="mb-3 text-xs opacity-90">
                      Storage quota reached. Please upgrade your plan or delete
                      existing models to free up space.
                    </div>
                    <div class="flex justify-center gap-2">
                      <StwButton
                        label="Upgrade"
                        color="#10b981"
                        size="xs"
                        @click="router.push('/pricing')"
                      />
                      <StwButton
                        label="My Dashboard"
                        color="gray"
                        size="xs"
                        @click="router.push('/dashboard')"
                      />
                    </div>
                  </template>

                  <div
                    v-else-if="storagePercentage >= 80"
                    class="text-xs opacity-90"
                  >
                    Storage is at {{ storagePercentage }}%. Consider upgrading
                    your plan or removing unused models.
                  </div>
                </div>

                <Message
                  v-if="isScanRequired && !hasScansRemaining"
                  severity="error"
                  class="mb-4"
                  :closable="false"
                  :pt="{
                    root: {
                      class:
                        'bg-red-900/20 border border-red-500/30 text-red-300',
                    },
                    icon: { class: 'text-red-400' },
                    text: { class: 'text-red-300' },
                  }"
                >
                  You have used all your available scans for this billing
                  period. Please wait for the next cycle or upgrade your plan
                  for more scans.
                </Message>

                <Message
                  v-if="imageCountTooFew"
                  severity="error"
                  class="mb-4"
                  :closable="false"
                  :pt="{
                    root: {
                      class:
                        'bg-red-900/20 border border-red-500/30 text-red-300',
                    },
                    icon: { class: 'text-red-400' },
                    text: { class: 'text-red-300' },
                  }"
                >
                  Number of selected images does not meet the minimum count of
                  {{ minImageCount }} images.
                </Message>

                <div class="flex justify-center gap-3">
                  <StwButton
                    icon="pi pi-trash"
                    label="Clear"
                    color="red"
                    @click="handleClear"
                  />
                  <StwButton
                    v-if="isAllowAddMoreFiles"
                    icon="pi pi-plus"
                    label="Add More Files"
                    color="blue"
                    @click="handleAddFile"
                  />
                  <StwButton
                    icon="pi pi-upload"
                    label="Upload"
                    color="#10b981"
                    :disabled="
                      isStorageFull ||
                      (isScanRequired &&
                        (!canPerformScan || isInvalidAspectRatio))
                    "
                    @click="handleUpload"
                  />
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="uploadedFiles.length > 0"
            class="flex w-full justify-center"
          >
            <div class="w-full max-w-md text-center">
              <div class="mb-4 text-lg font-bold">
                Uploaded File{{ uploadedFiles.length > 1 ? 's' : '' }}
              </div>
              <div
                class="bg-surface-900/50 rounded-xl p-6 shadow-sm ring-1 ring-white/10 backdrop-blur"
              >
                <template v-if="uploadedFiles.length === 1">
                  <div class="mb-2 break-all text-xl font-semibold">
                    {{ uploadedFiles[0].name }}
                  </div>
                  <div class="mb-2 text-sm opacity-80">
                    Type: {{ getFileTypeString(uploadedFiles[0]) }}
                  </div>
                  <div class="mb-4 text-sm opacity-80">
                    Size: {{ getFileSizeString(uploadedFiles[0].size) }}
                  </div>
                </template>

                <template v-else>
                  <div class="mb-4 max-h-40 overflow-y-auto text-left">
                    <div class="mb-2 text-sm font-semibold">
                      {{ uploadedFiles.length }} files uploaded:
                    </div>
                    <div
                      v-for="(file, index) in uploadedFiles"
                      :key="index"
                      class="mb-1 flex justify-between text-xs"
                    >
                      <span class="max-w-[80%] truncate">{{ file.name }}</span>
                      <span>{{ getFileSizeString(file.size) }}</span>
                    </div>
                  </div>
                </template>

                <Badge
                  value="Uploaded Successfully"
                  severity="success"
                  class="mb-4"
                  :pt="{
                    root: { class: 'bg-green-600 text-white' },
                  }"
                />
                <div class="flex justify-center">
                  <Button
                    icon="pi pi-times"
                    label="Clear"
                    severity="secondary"
                    :pt="{
                      root: {
                        class:
                          'bg-surface-700 border-surface-600 hover:bg-surface-600 hover:border-surface-500',
                      },
                    }"
                    @click="handleClear"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>

      <template #empty>
        <div
          class="bg-surface-900/40 flex h-full w-full cursor-pointer flex-col items-center justify-center rounded-xl p-8 ring-1 ring-white/10 backdrop-blur"
          @click="handleAddFile"
        >
          <i
            class="pi pi-cloud-upload !text-muted-color text-surface-0 !rounded-full !border-2 !p-8 !text-4xl"
          />
          <p class="text-surface-0 mb-0 mt-6 opacity-90">
            Click here, drag and drop, or paste files to upload
          </p>
        </div>
      </template>
    </FileUpload>
  </div>

  <FullscreenSpinner v-if="isLoading" />
</template>

<script setup lang="ts">
import type { QuotasData } from '@stw/types';
import { Badge, Button, FileUpload, Message, useConfirm } from 'primevue';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import { createItem, deleteItem, updateItem } from '@/api/item';
import {
  getImageFileUploadUrl,
  getImageUploadUrl,
  getVideoUploadUrl,
  startImageScan,
  startImageScanMeshy,
  startVideoScan,
  uploadToSignedUrl as uploadToBucketBySignedUrl,
} from '@/api/upload';
import { getQuotas } from '@/api/user';
import FullscreenSpinner from '@/components/FullscreenSpinner.vue';
import { StwButton, StwMessage } from '@/components/stw';
import { formatFileSize, formatStorageQuota } from '@/utils/byteFormat';
import { uploadFileInChunks } from '@/utils/chunk';
import { calculateQuotaDisplay } from '@/utils/quotaUtils';
import type { StwFetchError } from '@/utils/stwFetch';
import { useStwToast } from '@/utils/stwToast';

const MAX_DURATION_SECONDS = 180;

const props = defineProps<{
  engine: 'kiri' | 'meshy' | 'model';
}>();

const confirm = useConfirm();
const toast = useStwToast();
const router = useRouter();
const fileUploadRef = ref();
const files = ref<File[]>([]);
const isLoading = ref<boolean>(false);
const quotas = ref<QuotasData | null>(null);
const isInvalidAspectRatio = ref(false);

const emit = defineEmits<{
  uploadComplete: [];
  changeType: [];
}>();

const handleAddFile = () => {
  fileUploadRef.value.choose();
};

// Meshy and KIRI both scan a source (image/video) into a generated model, so
// neither accepts an already-made model file here - that lives solely in the
// 'model' engine below.
//
// TODO: KIRI's checkIsModel/createItem('model')/uploadFileInChunks branch
// further down this file still runs a direct .ply/.glb/.ksplat upload
// generically for whichever engine's MODEL_EXTENSIONS matches - it isn't
// KIRI-specific code, so nothing there needed to change to move direct
// model uploads to their own picker entry. Worth revisiting whether that
// shared branch should be pulled out of this component once there are two
// unrelated reasons to touch it.
const MODEL_EXTENSIONS = computed(() =>
  props.engine === 'model' ? (['.ply', '.glb', '.ksplat'] as const) : []
);

const acceptString = computed(() => {
  if (props.engine === 'meshy') return 'image/png, image/jpeg';
  if (props.engine === 'model') return '.ply, .glb, .ksplat';
  return 'image/*, video/*';
});

// Meshy only generates from a single image; KIRI needs a larger image set
// for photogrammetry.
const minImageCount = computed(() => (props.engine === 'meshy' ? 1 : 20));
const maxImageCount = computed(() => (props.engine === 'meshy' ? 1 : 300));
const MESHY_IMAGE_TYPES = ['image/png', 'image/jpeg'];

const checkIsModel = (file: File): boolean => {
  const filename = file.name.toLowerCase();
  return MODEL_EXTENSIONS.value.some((ext) => filename.endsWith(ext));
};

const checkIsImage = (file: File): boolean => {
  return file.type.startsWith('image/');
};

const checkIsVideo = (file: File): boolean => {
  return file.type.startsWith('video/');
};

const isAllowAddMoreFiles = computed(() => {
  if (files.value.some((file) => checkIsModel(file) || checkIsVideo(file))) {
    return false;
  }

  if (props.engine === 'meshy' && files.value.length >= maxImageCount.value) {
    return false;
  }

  return true;
});

const isScanRequired = computed(() => {
  return files.value.some((file) => checkIsVideo(file) || checkIsImage(file));
});

const hasScansRemaining = computed(() => {
  // Allow scan if quota is unlimited (-1) or has available total credits (plan + topup)
  return (
    quotas.value?.scan_quota === -1 ||
    (quotas.value?.total_scan_credit || 0) > 0
  );
});

const canPerformScan = computed(() => {
  return (
    isScanRequired.value &&
    hasScansRemaining.value &&
    !imageCountTooFew.value &&
    !isInvalidAspectRatio.value
  );
});

const imageCount = ref(0);
const imageCountTooFew = computed(() => {
  return imageCount.value !== 0 && imageCount.value < minImageCount.value;
});

const isStorageFull = computed(() => {
  if (!quotas.value) return false;
  // Block if quota is 0 (no storage) or if used >= quota (when quota > 0)
  // Allow if quota is negative (unlimited)
  return (
    quotas.value.storage_quota === 0 ||
    (quotas.value.storage_quota > 0 &&
      quotas.value.storage_used >= quotas.value.storage_quota)
  );
});

const storageDisplay = computed(() => {
  if (!quotas.value) return 'Loading...';
  return `${formatFileSize(quotas.value.storage_used)} / ${formatStorageQuota(quotas.value.storage_quota)}`;
});

const storagePercentage = computed(() => {
  if (!quotas.value || quotas.value.storage_quota === 0) return 0;
  return Math.min(
    Math.round((quotas.value.storage_used / quotas.value.storage_quota) * 100),
    100
  );
});

const displayDialog = (options: {
  header: string;
  message: string;
  acceptText?: string;
  acceptFunc?: () => any;
  hideReject?: boolean;
  rejectText?: string;
  rejectFunc?: () => any;
}) => {
  confirm.require({
    header: options.header,
    message: options.message,
    icon: 'pi pi-exclamation-circle',
    acceptLabel: options.acceptText ?? 'OK',
    rejectLabel: options.rejectText ?? 'Cancel',
    rejectClass: options.hideReject ? 'hidden' : undefined,
    accept: options.acceptFunc,
    reject: options.rejectFunc,
    // this is assuming closing dialog = reject
    onHide: options.rejectFunc,
  });
};

const handleSelect = async () => {
  const oldFiles = [...files.value];
  const currentFiles = fileUploadRef.value.files;

  const hasModelFiles = currentFiles.some((file: File) => {
    return checkIsModel(file);
  });
  const hasImageFiles = currentFiles.some((file: File) => {
    return checkIsImage(file);
  });
  const hasVideoFiles = currentFiles.some((file: File) => {
    return checkIsVideo(file);
  });

  const hasMixedFiles =
    (hasImageFiles && hasVideoFiles) ||
    (hasModelFiles && (hasImageFiles || hasVideoFiles));

  if (hasMixedFiles) {
    fileUploadRef.value.files = oldFiles;

    toast.add({
      severity: 'error',
      summary: 'Error!',
      detail: 'Cannot upload multiple types of files at the same time.',
    });

    return;
  }

  if (!isAllowAddMoreFiles.value) {
    fileUploadRef.value.files = oldFiles;

    toast.add({
      severity: 'error',
      summary: 'Error!',
      detail: 'Cannot upload more than one file of the selected type at once.',
    });

    return;
  }

  if (hasVideoFiles && props.engine === 'meshy') {
    fileUploadRef.value.files = oldFiles;

    toast.add({
      severity: 'error',
      summary: 'Error!',
      detail: 'Video upload is not supported for AI Generated Mesh.',
    });

    return;
  }

  if (hasVideoFiles) {
    let metadata;
    try {
      metadata = await getVideoMetadata(currentFiles[0]);
    } catch (error) {
      console.error('Error reading video metadata:', error);
      fileUploadRef.value.files = oldFiles; // Keep old files
      displayDialog({
        header: 'Unable to read video.',
        message:
          "Your browser couldn't read this video. Try a different file, or use a different browser/device.",
        acceptText: 'OK',
        hideReject: true,
      });
      return;
    }

    isInvalidAspectRatio.value = false; // Reset before check

    const landscapeAspectRatio = 16 / 9; // 1.777...
    const portraitAspectRatio = 9 / 16; // 0.5625
    const actualAspectRatio = metadata.aspectRatio;

    const tolerance = 0.02;

    const isValidLandscape =
      Math.abs(actualAspectRatio - landscapeAspectRatio) <= tolerance;
    const isValidPortrait =
      Math.abs(actualAspectRatio - portraitAspectRatio) <= tolerance;

    if (!isValidLandscape && !isValidPortrait) {
      isInvalidAspectRatio.value = true;
      fileUploadRef.value.files = oldFiles; // Keep old files
      displayDialog({
        header: 'Currently not supported.',
        message: `Only 16:9 (landscape or portrait) videos are supported.`,
        acceptText: 'OK',
        hideReject: true,
        acceptFunc: () => {
          // User acknowledged, files are already reverted
        },
        rejectFunc: () => {
          // Should not happen with hideReject: true
        },
      });
      return;
    }

    if (metadata.duration > MAX_DURATION_SECONDS) {
      // Always inform the user that videos longer than 3 minutes will be trimmed
      toast.add({
        severity: 'info',
        summary: 'Video will be trimmed.',
        detail:
          'Only the first 3 minutes of the video will be used to generate the 3D model.',
      });

      files.value = [...fileUploadRef.value.files];
    }
  } else if (hasImageFiles) {
    if (
      props.engine === 'meshy' &&
      currentFiles.some((file: File) => !MESHY_IMAGE_TYPES.includes(file.type))
    ) {
      fileUploadRef.value.files = oldFiles;
      displayDialog({
        header: 'Unsupported File Type',
        message: 'Only JPG or PNG images are supported for AI Generated Mesh.',
        acceptText: 'OK',
        hideReject: true,
      });
      return;
    }

    if (currentFiles.length > maxImageCount.value) {
      fileUploadRef.value.files = oldFiles;
      displayDialog({
        header: 'Too Many Images',
        message:
          props.engine === 'meshy'
            ? 'Only 1 image is supported for AI Generated Mesh.'
            : `Number of selected images exceeded the maximum of ${maxImageCount.value} files.`,
        acceptText: 'OK',
        hideReject: true,
      });
      return;
    } else imageCount.value = currentFiles.length;
  }

  files.value = [...fileUploadRef.value.files];
};

// accept only takes '.ext' tokens and mime patterns ('image/*', 'video/*',
// or an exact type like 'image/png') - matches what PrimeVue's own accept
// validation understands, since pasted files bypass that (they're injected
// directly into fileUploadRef.files rather than going through its normal
// picker/drag-drop input).
const isFileAccepted = (file: File): boolean => {
  const filename = file.name.toLowerCase();

  return acceptString.value.split(',').some((rawToken) => {
    const token = rawToken.trim();
    if (token.startsWith('.')) return filename.endsWith(token);
    if (token.endsWith('/*')) return file.type.startsWith(token.slice(0, -1));
    return file.type === token;
  });
};

const handlePaste = (event: ClipboardEvent) => {
  if (isLoading.value) return;

  const items = event.clipboardData?.items;
  if (!items) return;

  const pastedFiles = Array.from(items)
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => file !== null);

  if (pastedFiles.length === 0) return;

  event.preventDefault();

  const acceptedFiles = pastedFiles.filter(isFileAccepted);

  if (acceptedFiles.length === 0) {
    toast.add({
      severity: 'error',
      summary: 'Error!',
      detail: 'Pasted file type is not supported for this upload type.',
    });
    return;
  }

  fileUploadRef.value.files = [...fileUploadRef.value.files, ...acceptedFiles];
  handleSelect();
};

const handleClear = () => {
  files.value.length = 0;
  fileUploadRef.value.clear();
};

const handleUpload = async () => {
  // Check if storage quota already reached
  if (
    quotas.value &&
    (quotas.value.storage_quota === 0 ||
      (quotas.value.storage_quota > 0 &&
        quotas.value.storage_used >= quotas.value.storage_quota))
  ) {
    confirm.require({
      header: 'Storage Limit Reached',
      message:
        'You have reached your storage limit. Please upgrade your plan or delete existing models to free up space.',
      icon: 'pi pi-exclamation-triangle',
      rejectLabel: 'Go to Dashboard',
      acceptLabel: 'View Plans',
      accept: () => {
        router.push('/pricing');
      },
      reject: () => {
        router.push('/dashboard');
      },
    });

    return;
  }

  let item_id: string | undefined;
  const file: File = files.value[0];
  const ext = file.name.split('.').pop();

  if (!ext) {
    return console.error('File extension not found.');
  }

  isLoading.value = true;

  try {
    if (checkIsModel(file)) {
      const { item_id: createdItemId } = await createItem(ext, 'model');
      item_id = createdItemId;

      // Use client-side chunking for model uploads
      await uploadFileInChunks(createdItemId, fileUploadRef.value.files[0]);

      await updateItem(createdItemId, {
        name: file.name.replace(/\.[^/.]+$/, ''),
      });

      showUploadSuccess(false);
      router.push(`/item/${createdItemId}`);
    } else if (checkIsVideo(file)) {
      const fileName = file.name.replace(/\.[^/.]+$/, '');
      const { item_id: createdItemId } = await createItem(
        'ply',
        'video',
        fileName
      );
      item_id = createdItemId;

      const { path, signedUrl: url } = await getVideoUploadUrl(createdItemId);

      await uploadToBucketBySignedUrl(url, file);

      const metadata = await getVideoMetadata(file);

      const { duration, width, height } = metadata;

      await startVideoScan(createdItemId, duration, width, height, path);

      showUploadSuccess(true);

      router.push(`/dashboard`);
    } else if (checkIsImage(file)) {
      const imageFiles = files.value;
      const baseImageName = imageFiles[0].name.replace(/\.[^/.]+$/, '');
      const { item_id: createdItemId } = await createItem(
        props.engine === 'meshy' ? 'glb' : 'ply',
        'image',
        // Meshy only ever takes a single image - "(Image Set)" only makes
        // sense for KIRI's multi-image scans
        props.engine === 'meshy'
          ? baseImageName
          : `${baseImageName} (Image Set)`
      );
      item_id = createdItemId;

      if (props.engine === 'meshy') {
        // Meshy downloads a single specific file object (not a folder listing
        // like KIRI), so it needs that one file's own bucket path, not the
        // batch folder prefix below.
        const singleFile = imageFiles[0];
        const { path, signedUrl } = await getImageFileUploadUrl(
          createdItemId,
          singleFile.name
        );

        await uploadToBucketBySignedUrl(signedUrl, singleFile);

        await startImageScanMeshy(createdItemId, path);
      } else {
        // Get upload URL for images
        const { path } = await getImageUploadUrl(createdItemId);

        // Upload all image files to the batch upload URL
        const uploadPromises = imageFiles.map(async (file, index) => {
          // Generate numbered filename like image-001.jpg, image-002.jpg, etc.
          const paddedIndex = String(index + 1).padStart(3, '0');
          const fileExtension = file.name.split('.').pop();
          const numberedFilename = `image-${paddedIndex}.${fileExtension}`;

          // Get individual signed URL for each file
          const { signedUrl } = await getImageFileUploadUrl(
            createdItemId,
            numberedFilename
          );

          await uploadToBucketBySignedUrl(signedUrl, file);
        });

        await Promise.all(uploadPromises);

        await startImageScan(createdItemId, path);
      }

      showUploadSuccess(true);

      router.push(`/dashboard`);
    }

    emit('uploadComplete');
  } catch (error) {
    console.error('Upload failed:', error);

    const fetchError = error as StwFetchError;

    // Check for storage quota error from backend
    if (fetchError.message?.includes('Storage quota exceeded')) {
      confirm.require({
        header: 'Storage Limit Reached',
        message:
          'You have reached your storage limit. Please upgrade your plan or delete existing models to free up space.',
        icon: 'pi pi-exclamation-triangle',
        rejectLabel: 'Go to Dashboard',
        acceptLabel: 'View Plans',
        accept: () => {
          router.push('/pricing');
        },
        reject: () => {
          router.push('/dashboard');
        },
      });
    } else {
      // Generic error toast
      toast.add({
        severity: 'error',
        summary: 'Error!',
        detail: 'File not uploaded. Error: ' + fetchError.message,
      });
    }

    if (item_id) {
      await deleteItem(item_id);
    }
  } finally {
    isLoading.value = false;
  }
};

const fetchUserQuotas = async () => {
  try {
    quotas.value = await getQuotas();
  } catch (error) {
    console.error('Failed to fetch quotas:', error);
    toast.add({
      severity: 'error',
      summary: 'Error!',
      detail: 'Could not fetch user scan quotas.',
    });
  }
};

const getFileTypeString = (file: File): string => {
  const filename = file.name.toLowerCase();

  if (filename.endsWith('.glb')) {
    return '3D Model (.GLB)';
  }

  if (filename.endsWith('.ply')) {
    return '3D Model (.PLY)';
  }

  if (filename.endsWith('.ksplat')) {
    return 'Compressed 3D Model (.KSPLAT)';
  }

  const mimeType = file.type;

  if (mimeType.startsWith('image/')) {
    return `${mimeType.split('/')[1].toUpperCase()} Image`;
  }

  if (mimeType.startsWith('video/')) {
    return `${mimeType.split('/')[1].toUpperCase()} Video`;
  }

  const ext = filename.split('.').pop();
  return `${ext?.toUpperCase() || 'Unknown'} file`;
};

const getFileSizeString = (bytes: number): string => {
  if (bytes === 0) {
    return '0 B';
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Format the number with max 2 decimals
  const formattedSize = new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2,
  }).format(bytes / Math.pow(k, i));

  return `${formattedSize} ${units[i]}`;
};

const showUploadSuccess = (isProcessing = true) => {
  const processingTime = props.engine === 'meshy' ? '5–15' : '20–40';

  toast.add({
    severity: 'success',
    summary: 'Upload success.',
    detail: isProcessing
      ? `Your upload is processing. This may take ~${processingTime} minutes.`
      : 'Your upload was successful.',
  });
};

const getVideoMetadata = (
  file: File
): Promise<{
  duration: number;
  width: number;
  height: number;
  aspectRatio: number;
}> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    const cleanup = () => {
      clearTimeout(timeoutId);
      window.URL.revokeObjectURL(video.src);
    };

    // Some browsers neither fire `error` nor `loadedmetadata` for a codec
    // they can't decode (observed with H.265/HEVC on some Windows setups) -
    // without this the promise just hangs forever with no user feedback.
    const timeoutId = setTimeout(() => {
      cleanup();
      reject('Timed out reading video metadata.');
    }, 10000);

    video.onloadedmetadata = () => {
      cleanup();
      if (!video.videoWidth || !video.videoHeight) {
        reject('Error loading video metadata.');
        return;
      }
      resolve({
        duration: video.duration,
        width: video.videoWidth,
        height: video.videoHeight,
        aspectRatio: video.videoWidth / video.videoHeight,
      });
    };

    video.onerror = () => {
      cleanup();
      reject('Error loading video metadata.');
    };

    video.src = URL.createObjectURL(file);
  });
};

watch(
  isScanRequired,
  (newVal) => {
    if (newVal && !quotas.value) {
      fetchUserQuotas();
    }
  },
  { immediate: true }
);

onMounted(() => {
  // Always fetch quotas on mount to show storage quota for all upload types
  fetchUserQuotas();

  // Window-level rather than on the dropzone itself: this component is the
  // whole upload dialog's content while it's open, and PrimeVue's dropzone
  // doesn't receive focus/paste targeting on its own.
  window.addEventListener('paste', handlePaste);
});

onUnmounted(() => {
  window.removeEventListener('paste', handlePaste);
});
</script>

<style scoped>
/* Ensure empty container and its content take full height */
:deep(.p-fileupload-content) {
  display: flex;
  flex-direction: column;
  flex: 1;
  width: 100%;
  min-height: 0;
}

:deep(.p-fileupload-content [data-pc-section='empty']) {
  display: flex;
  flex: 1;
  width: 100%;
  min-height: 0;
  align-items: center;
  justify-content: center;
}

.upload-empty-area {
  height: 100%;
  min-height: 100%;
}
</style>
